import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAdminContactRequests } from "@/hooks/useContactRequests";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Check, X, Loader2, User, Building2, MessageSquare, Clock } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { useState } from "react";
import { logAudit } from "@/lib/auditLog";
import { useAdminOrgContext } from "@/hooks/useAdminOrgContext";
import { CONTACT_STATUS_LABELS, CONTACT_STATUS_COLORS } from "@/lib/constants";

const TYPE_LABELS: Record<string, string> = {
  vaga: "Vaga",
  entrevista_exploratoria: "Entrevista Exploratória",
  mentoria: "Mentoria",
  networking: "Networking",
  proposta: "Proposta",
};

export default function AdminPedidoContatoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { updateStatus } = useAdminContactRequests();
  const { orgId } = useAdminOrgContext();
  const [adminNote, setAdminNote] = useState("");

  const { data: request, isLoading } = useQuery({
    queryKey: ["admin_contact_request_detail", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contact_requests")
        .select("*, partner_orgs(name, industry), profiles!contact_requests_user_id_fkey(full_name, objective, city, state, bio)")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="p-8 text-center">
        <p className="text-muted-foreground">Pedido não encontrado.</p>
        <Button variant="ghost" className="mt-4" onClick={() => navigate("/admin/pedidos-contato")}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
        </Button>
      </div>
    );
  }

  const partner = request.partner_orgs as any;
  const student = request.profiles as any;

  const handleApprove = async () => {
    try {
      await updateStatus.mutateAsync({ id: request.id, status: "waiting_student", adminNote: adminNote.trim() || undefined });
      if (orgId) {
        await logAudit({ orgId, action: "approve_contact", entityType: "contact_request", entityId: request.id });
      }
      toast({ title: "Pedido aprovado", description: "O aluno será notificado para aceitar o contato." });
      navigate("/admin/pedidos-contato");
    } catch {
      toast({ title: "Erro ao aprovar", variant: "destructive" });
    }
  };

  const handleReject = async () => {
    try {
      await updateStatus.mutateAsync({ id: request.id, status: "declined", adminNote: adminNote.trim() || "Rejeitado pelo admin" });
      if (orgId) {
        await logAudit({ orgId, action: "reject_contact", entityType: "contact_request", entityId: request.id });
      }
      toast({ title: "Pedido rejeitado" });
      navigate("/admin/pedidos-contato");
    } catch {
      toast({ title: "Erro ao rejeitar", variant: "destructive" });
    }
  };

  return (
    <div className="p-8 max-w-4xl">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate("/admin/pedidos-contato")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Detalhe do Pedido</h1>
          <p className="text-muted-foreground text-sm">
            #{request.id.slice(0, 8)} • {format(new Date(request.created_at), "dd/MM/yyyy HH:mm")}
          </p>
        </div>
        <span className={`ml-auto text-xs px-3 py-1 rounded-full font-medium ${CONTACT_STATUS_COLORS[request.status] ?? "bg-muted text-muted-foreground"}`}>
          {CONTACT_STATUS_LABELS[request.status] ?? request.status}
        </span>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Partner info */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-card rounded-2xl p-6 border border-border">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground">Parceiro</h2>
              <p className="text-sm text-muted-foreground">{partner?.name ?? "—"}</p>
            </div>
          </div>
          {partner?.industry && <p className="text-sm text-muted-foreground">Setor: {partner.industry}</p>}
          <div className="mt-3 space-y-1 text-sm text-muted-foreground">
            <p><strong>Tipo:</strong> {TYPE_LABELS[request.contact_type] ?? request.contact_type}</p>
            <p><strong>Urgência:</strong> {request.urgency === "alta" ? "🔴 Alta" : request.urgency === "media" ? "🟡 Média" : "⚪ Baixa"}</p>
          </div>
        </motion.div>

        {/* Student info */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="bg-card rounded-2xl p-6 border border-border">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-accent/50 flex items-center justify-center">
              <User className="w-5 h-5 text-foreground" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground">Talento</h2>
              <p className="text-sm text-muted-foreground">{student?.full_name ?? "—"}</p>
            </div>
          </div>
          {student?.city && <p className="text-sm text-muted-foreground">{[student.city, student.state].filter(Boolean).join(", ")}</p>}
          {student?.objective && <p className="text-sm text-muted-foreground mt-1">{student.objective}</p>}
          {student?.bio && <p className="text-xs text-muted-foreground mt-2 line-clamp-3">{student.bio}</p>}
        </motion.div>
      </div>

      {/* Message */}
      {request.message && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="mt-6 bg-card rounded-2xl p-6 border border-border">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="w-4 h-4 text-muted-foreground" />
            <h3 className="font-semibold text-foreground text-sm">Mensagem do Parceiro</h3>
          </div>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{request.message}</p>
        </motion.div>
      )}

      {/* Student decision (if exists) */}
      {request.student_decision && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="mt-4 bg-card rounded-2xl p-6 border border-border">
          <h3 className="font-semibold text-foreground text-sm mb-2">Decisão do Aluno</h3>
          <p className="text-sm">
            <Badge variant={request.student_decision === "accepted" ? "ready" : "construction"}>
              {request.student_decision === "accepted" ? "Aceito" : "Recusado"}
            </Badge>
            {request.student_decision_at && (
              <span className="text-xs text-muted-foreground ml-2">
                em {format(new Date(request.student_decision_at), "dd/MM/yyyy HH:mm")}
              </span>
            )}
          </p>
          {request.student_reason && <p className="text-sm text-muted-foreground mt-2">Motivo: {request.student_reason}</p>}
        </motion.div>
      )}

      {/* Admin note */}
      {request.admin_note && request.status !== "requested" && (
        <div className="mt-4 bg-muted/50 rounded-xl p-4 text-sm text-muted-foreground">
          <strong className="text-foreground">Nota do admin:</strong> {request.admin_note}
        </div>
      )}

      {/* Actions */}
      {request.status === "requested" && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="mt-6 bg-card rounded-2xl p-6 border border-primary/20">
          <h3 className="font-semibold text-foreground mb-3">Decisão</h3>
          <Textarea
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            placeholder="Nota do admin (opcional)..."
            rows={2}
            className="mb-4"
          />
          <div className="flex gap-3">
            <Button onClick={handleApprove} disabled={updateStatus.isPending}>
              <Check className="w-4 h-4 mr-1" /> Aprovar
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={updateStatus.isPending}>
              <X className="w-4 h-4 mr-1" /> Rejeitar
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
